// Renders a single CustomerHelp article at /help/<surface>/<slug>.
//
// Public route — no auth gate, because owner, operator, pro, and lead
// audiences all land here (Rosie citations, direct links, owner
// lookup, App Store referrers). Article body comes from
// public.customer_help_article_by_slug, which returns markdown plus
// the per-location config + kira_knowledge topics so the renderer
// can interpolate {{location.*}} / {{kb.*}} tokens before handing
// the markdown to react-markdown.
//
// Location resolution priority (lowest-cost wins):
//   1. ?location= query param  (Rosie citations carry this when she
//      knows the operator's location)
//   2. localStorage hub.activeLocationId  (sticky from a prior signed-in
//      Hub session)
//   3. null  (anonymous lead with no context — article renders without
//      per-location interpolation; any {{location.*}} tokens collapse
//      to empty string per the renderer contract)

import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useHelpArticle, interpolate, surfaceLabel } from '@/lib/help';

function resolveLocationId(qsLocation: string | null): string | null {
  if (qsLocation) return qsLocation;
  try {
    return localStorage.getItem('hub.activeLocationId');
  } catch {
    return null;
  }
}

export default function HelpArticle() {
  const params = useParams();
  const [qs] = useSearchParams();

  const slug = params['*'] ?? '';
  const locationId = resolveLocationId(qs.get('location'));

  const { data: article, isLoading, isError, error } = useHelpArticle(slug || null, locationId);

  const body = useMemo(() => {
    if (!article) return '';
    return interpolate(article.body_md, {
      location: article.location_config,
      kb: article.kb_topics,
    });
  }, [article]);

  return (
    <div className="min-h-screen bg-brand-fog">
      <header className="px-6 pt-12 pb-6 text-center border-b border-brand-mist">
        <a href="/" className="inline-block">
          <h1 className="font-display text-3xl font-extrabold text-image-black">IMAGE <span className="text-image-pink">OS</span></h1>
        </a>
        <p className="text-sm text-brand-slate mt-1">
          <Link to="/help" className="hover:text-image-pink">Help</Link>
          {article ? <> &middot; {surfaceLabel(article.surface)}</> : null}
        </p>
      </header>

      <main className="px-6 py-10 max-w-3xl mx-auto">
        {isLoading && <p className="text-sm text-brand-slate">Loading…</p>}

        {isError && (
          <div className="text-sm text-brand-slate">
            <p>We couldn’t load this article.</p>
            <p className="text-xs text-brand-slate/70 mt-2">
              {(error as Error)?.message ?? 'Unknown error'}
            </p>
            <p className="mt-4">
              <Link to="/help" className="text-image-pink hover:text-image-pink-deep">
                &larr; Back to all articles
              </Link>
            </p>
          </div>
        )}

        {!isLoading && !isError && !article && (
          <div className="text-sm text-brand-slate">
            <p>We don’t have an article at <code>{slug}</code> yet.</p>
            <p className="mt-4">
              <Link to="/help" className="text-image-pink hover:text-image-pink-deep">
                &larr; Browse all articles
              </Link>
            </p>
          </div>
        )}

        {article && (
          <article className="prose prose-sm sm:prose-base prose-headings:font-display prose-headings:text-brand-deep prose-p:text-brand-slate prose-li:text-brand-slate prose-a:text-image-pink hover:prose-a:text-image-pink-deep">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
          </article>
        )}
      </main>

      <footer className="px-6 py-8 max-w-3xl mx-auto text-center border-t border-brand-mist">
        <Link to="/help" className="text-sm text-brand-slate hover:text-image-pink">
          &larr; All help articles
        </Link>
      </footer>
    </div>
  );
}
